import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useSettingsStore } from '@/stores/settingsStore'
import { AuthBackground } from '@/components/auth/AuthBackground'
import { MorphSelect } from '@/components/ui/MorphSelect'
import { LANGUAGE_OPTIONS } from '@/components/ui/languageOptions'
import type { Locale } from '@/types'
import logoNameDark from '@/assets/logo_name_dark.png'
import logoNameLight from '@/assets/logo_name_light.png'

interface AuthLayoutProps {
  title: string
  subtitle?: string
  children: ReactNode
  footer?: ReactNode
  maxWidth?: number
}

export function AuthLayout({ title, subtitle, children, footer, maxWidth = 420 }: AuthLayoutProps) {
  const { t } = useTranslation()
  const resolvedTheme = useSettingsStore((s) => s.resolvedTheme)
  const language = useSettingsStore((s) => s.language)
  const setLanguage = useSettingsStore((s) => s.setLanguage)

  return (
    <AuthBackground>
      <div
        style={{
          width: '100%',
          maxWidth,
          background: resolvedTheme === 'dark' ? 'rgba(2,6,23,.86)' : 'rgba(255,255,255,.97)',
          border: resolvedTheme === 'dark'
            ? '1px solid rgba(99,102,241,.16)'
            : '1px solid rgba(148,163,184,.34)',
          borderRadius: 14,
          padding: 32,
          boxShadow: resolvedTheme === 'dark'
            ? '0 0 0 1px rgba(99,102,241,.06), 0 24px 80px rgba(2,6,23,.55)'
            : '0 24px 80px rgba(15,23,42,.14)',
          backdropFilter: 'blur(18px)',
          display: 'flex',
          flexDirection: 'column',
          gap: 24,
        }}
      >
        <header style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <img
              src={resolvedTheme === 'dark' ? logoNameDark : logoNameLight}
              alt="MorphERD"
              style={{ height: 28, width: 'fit-content' }}
            />
            <MorphSelect<Locale>
              value={language}
              ariaLabel={t('footer.languageAria')}
              options={LANGUAGE_OPTIONS}
              onChange={(nextLanguage) => {
                if (nextLanguage) setLanguage(nextLanguage)
              }}
              size="sm"
              className="w-[126px] shrink-0"
              minMenuWidth={138}
            />
          </div>
          <h1 style={{ margin: 0, fontSize: 24 }}>{title}</h1>
          {subtitle ? (
            <p style={{ margin: 0, fontSize: 14, opacity: 1, color: 'var(--text-muted)' }}>{subtitle}</p>
          ) : null}
        </header>
        {children}
        {footer ? <footer style={{ fontSize: 13, opacity: 1, color: 'var(--text-muted)' }}>{footer}</footer> : null}
      </div>
    </AuthBackground>
  )
}

export const authFormStyles = {
  field: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 6,
  },
  label: {
    fontSize: 13,
    fontWeight: 500,
    opacity: 1,
    color: 'var(--text)',
  },
  input: {
    background: 'var(--bg-input)',
    border: '1px solid var(--border)',
    borderRadius: 8,
    padding: '10px 12px',
    color: 'var(--text)',
    fontSize: 14,
    outline: 'none',
  },
  button: {
    background: '#3b82f6',
    border: 'none',
    color: 'white',
    borderRadius: 8,
    padding: '10px 12px',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  },
  error: {
    background: 'var(--color-danger-bg)',
    border: '1px solid var(--color-danger-border)',
    color: 'var(--color-danger)',
    borderRadius: 8,
    padding: '8px 12px',
    fontSize: 13,
  },
}
